/** @jsxImportSource jsx-dom */

import { select, selectAll } from "@fruugo/utilities";
import { i18n, i18nRich, waitForTranslations } from "./i18n";

const storageKey = "localeTooltipDismissed";
const triggerSelector = ".js-open-locale-picker";
const anchorSelector = `.header ${triggerSelector}`;
const tooltipClass = "js-locale-tooltip";
// Keep the bubble this far from the viewport edges, matching the site's 16px gutter
const viewportGutter = 16;

let reposition: (() => void) | undefined;

/**
 * Tell shoppers which store and language they're on when it isn't the country we geo-locate them to,
 * pointing at the header flag that opens the picker.
 */
export async function initLocaleTooltip() {
  const { currentCountry, geoIPCountry } = window;
  if (!geoIPCountry || geoIPCountry === currentCountry || isDismissed()) {
    return;
  }

  const anchor = select<HTMLElement>(anchorSelector);
  const container = anchor?.closest("li");
  if (!anchor || !container) return;

  await waitForTranslations().catch(() => undefined);

  const tooltip = (
    <LocaleTooltip onClose={dismissLocaleTooltip} />
  ) as HTMLElement;
  container.classList.add("tw:relative");
  container.appendChild(tooltip);

  reposition = () => positionTooltip(tooltip, anchor, container);
  reposition();
  window.addEventListener("resize", reposition);

  // Opening the picker from the header or footer means the shopper no longer needs pointing at it
  selectAll(triggerSelector)?.forEach((trigger) =>
    trigger.addEventListener("click", dismissLocaleTooltip, { once: true })
  );
}

export function dismissLocaleTooltip() {
  select(`.${tooltipClass}`)?.remove();
  if (reposition) window.removeEventListener("resize", reposition);
  reposition = undefined;
  try {
    localStorage.setItem(storageKey, "true");
  } catch (error) {
    // Without storage it will just show again on the next page
  }
}

function isDismissed() {
  try {
    return localStorage.getItem(storageKey) === "true";
  } catch (error) {
    return false;
  }
}

const LocaleTooltip = ({ onClose }: { onClose: () => void }) => {
  const countryName = window.currentCountryName || window.currentCountry;
  const languageName = i18n(`common.language.${window.currentLang}`);

  return (
    <div
      class={`${tooltipClass} tw:absolute tw:top-full tw:mt-3 tw:z-[900] tw:w-[302px] tw:max-w-[calc(100vw-2rem)] tw:flex tw:items-start tw:gap-[10px] tw:p-4 tw:rounded-[5px] tw:bg-black tw:text-white tw:text-base tw:text-start tw:font-normal`}
      data-testid="locale-tooltip"
    >
      <img
        class="js-locale-tooltip-arrow tw:absolute tw:-top-[7.2px] tw:-translate-x-1/2"
        src="/marketplace/images/tooltip-arrow.svg"
        width="15"
        height="7.2"
        alt=""
      />
      <p class="tw:m-0 tw:flex-1" role="status">
        {i18nRich("localepicker.tooltip", [
          <strong>{countryName}</strong>,
          <strong>{languageName}</strong>,
        ])}
      </p>
      <button
        type="button"
        class="tw:shrink-0 tw:p-0 tw:border-0 tw:bg-transparent tw:cursor-pointer tw:rounded-sm tw:focus-visible:ring-2 tw:focus-visible:ring-white"
        aria-label={i18n("search.page.close")}
        data-testid="locale-tooltip-close"
        onClick={onClose}
      >
        <img
          src="/marketplace/images/tooltip-close.svg"
          width="24"
          height="24"
          alt=""
        />
      </button>
    </div>
  );
};

// Centre the bubble under the flag, but keep it on screen; the arrow always points at the flag
function positionTooltip(
  tooltip: HTMLElement,
  anchor: HTMLElement,
  container: HTMLElement
) {
  const anchorRect = anchor.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const width = tooltip.offsetWidth;
  const anchorCentre = anchorRect.left + anchorRect.width / 2;

  const maxLeft = window.innerWidth - width - viewportGutter;
  const left = Math.max(
    viewportGutter,
    Math.min(anchorCentre - width / 2, maxLeft)
  );

  tooltip.style.left = `${left - containerRect.left}px`;

  const arrow = tooltip.querySelector<HTMLElement>(".js-locale-tooltip-arrow");
  if (arrow) arrow.style.left = `${anchorCentre - left}px`;
}
